/**
 * Reformat saved citations into a different style.
 *
 * Saved citations keep their parsed `entry` alongside the rendered text, which means
 * a bibliography can be re-rendered in any style without re-fetching a single source.
 * Nothing used that until now: citations were frozen in whatever style happened to be
 * selected when they were added, so "my professor wants MLA, not APA" meant deleting
 * and re-citing every source by hand, and a list built over several sittings could
 * silently end up in three different styles.
 *
 * Batching matters here. One HTTP request per citation is 40 round trips and 40 style
 * resolutions for a normal reading list; `/api/cite` takes an `entries` array and
 * resolves the style once, so this sends ceil(n / RESTYLE_BATCH_SIZE) requests.
 */
import type { EditableEntry, SavedCitation } from "@/lib/bibliography";

/**
 * Citations per request. Below the server's 50-entry cap, and small enough that the
 * progress callback moves often enough to look alive on a big bibliography.
 */
export const RESTYLE_BATCH_SIZE = 25;

export interface RestyleOptions {
  /** Injected for tests; defaults to global fetch. */
  fetchImpl?: typeof fetch;
  /** CSL output language to preserve while rendering saved entries. */
  lang?: string;
  /** Called after each batch with how many citations have been attempted. */
  onProgress?: (done: number, total: number) => void;
  signal?: AbortSignal;
}

/** Split into fixed-size chunks, preserving order. */
export function chunk<T>(items: T[], size: number): T[][] {
  if (size < 1) {
    throw new Error("chunk size must be at least 1");
  }
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/**
 * Format a list of parsed entries in one style, batched.
 *
 * Returns one slot per input entry, in order, with `null` where that entry could not
 * be rendered. A batch that fails outright marks only its own entries as failed, so
 * one bad response never discards the rest of the work.
 */
export async function formatEntries(
  entries: EditableEntry[],
  style: string,
  options: RestyleOptions = {}
): Promise<(string | null)[]> {
  const { fetchImpl = fetch, lang, onProgress, signal } = options;
  const out: (string | null)[] = [];
  let done = 0;

  for (const batch of chunk(entries, RESTYLE_BATCH_SIZE)) {
    let citationsOut: (string | null)[] = [];
    try {
      const response = await fetchImpl("/api/cite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entries: batch,
          style,
          ...(lang ? { lang } : {}),
        }),
        signal,
      });
      if (response.ok) {
        const data = (await response.json()) as {
          citations?: (string | null)[];
        };
        citationsOut = Array.isArray(data.citations) ? data.citations : [];
      }
    } catch {
      citationsOut = [];
    }

    for (let index = 0; index < batch.length; index++) {
      const text = citationsOut[index];
      out.push(typeof text === "string" && text.trim() ? text : null);
    }

    done += batch.length;
    onProgress?.(done, entries.length);
  }

  return out;
}

/**
 * Format every citation in `citations` into `style`.
 *
 * Resolves to a map of citation id → new text, with `null` where that citation could
 * not be rendered in the target style.
 */
export async function restyleCitations(
  citations: SavedCitation[],
  style: string,
  options: RestyleOptions = {}
): Promise<Record<string, string | null>> {
  const formatted = await formatEntries(
    citations.map((item) => item.entry),
    style,
    options
  );
  const result: Record<string, string | null> = {};
  citations.forEach((item, index) => {
    result[item.id] = formatted[index] ?? null;
  });
  return result;
}
